import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment, Order } from './arduino-generator.js';
import { ensureI2sAudioBus } from './beginner-i2s-audio.js';

const LOUD_THRESHOLD = 50;
const SAMPLE_COUNT = 256;

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function micHelperCode(): string {
  return [
    `#define SKYROVER_MIC_LOUD_THRESHOLD ${LOUD_THRESHOLD}`,
    `#define SKYROVER_MIC_SAMPLE_COUNT ${SAMPLE_COUNT}`,
    '',
    'int skyroverMicSoundLevel() {',
    '  if (!skyroverI2sAudioReady) {',
    '    return 0;',
    '  }',
    '  int16_t samples[SKYROVER_MIC_SAMPLE_COUNT];',
    '  size_t bytesRead = 0;',
    '  esp_err_t result = i2s_read(',
    '    SKYROVER_I2S_PORT,',
    '    (void*)samples,',
    '    sizeof(samples),',
    '    &bytesRead,',
    '    portMAX_DELAY',
    '  );',
    '  if (result != ESP_OK || bytesRead == 0) {',
    '    return 0;',
    '  }',
    '  int sampleCount = bytesRead / sizeof(int16_t);',
    '  double sumSquares = 0;',
    '  for (int i = 0; i < sampleCount; i++) {',
    '    double sample = (double)samples[i];',
    '    sumSquares += sample * sample;',
    '  }',
    '  double rms = sqrt(sumSquares / sampleCount);',
    '  int level = (int)constrain(rms / 200.0, 0.0, 100.0);',
    '  return level;',
    '}',
    '',
    'bool skyroverMicIsLoud() {',
    '  return skyroverMicSoundLevel() >= SKYROVER_MIC_LOUD_THRESHOLD;',
    '}',
  ].join('\n');
}

function ensureMicrophoneSupport(): void {
  ensureI2sAudioBus();
  arduinoGenerator.addGlobal(micHelperCode());
}

export function registerBeginnerMicrophoneBlockGenerators(): void {
  arduinoGenerator.forBlock['beginner_mic_is_loud'] = (_block: Block) => {
    ensureMicrophoneSupport();
    return ['skyroverMicIsLoud()', Order.FUNCTION_CALL];
  };

  arduinoGenerator.forBlock['beginner_mic_sound_level'] = (_block: Block) => {
    ensureMicrophoneSupport();
    return ['skyroverMicSoundLevel()', Order.FUNCTION_CALL];
  };

  arduinoGenerator.forBlock['beginner_mic_wait_loud'] = (block: Block) => {
    ensureMicrophoneSupport();
    return statementPrefix(
      block,
      ['while (!skyroverMicIsLoud()) {', '  delay(10);', '}', ''].join('\n'),
    );
  };

  arduinoGenerator.forBlock['beginner_mic_when_loud'] = (block: Block) => {
    ensureMicrophoneSupport();
    const branch = arduinoGenerator.statementToCode(block, 'DO');
    arduinoGenerator.addGlobal('bool skyroverMicWasLoud = false;');
    return statementPrefix(
      block,
      [
        '{',
        '  bool skyroverMicLoudNow = skyroverMicIsLoud();',
        '  if (!skyroverMicWasLoud && skyroverMicLoudNow) {',
        branch || '',
        '  }',
        '  skyroverMicWasLoud = skyroverMicLoudNow;',
        '}',
        '',
      ].join('\n'),
    );
  };
}

registerBeginnerMicrophoneBlockGenerators();
